import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import {
  X,
  Check,
  Calendar,
  User,
  Filter,
  RotateCcw
} from 'lucide-react';

/**
 * TicketFilters Component
 * Comprehensive filtering system for support tickets
 * 
 * Props:
 * - filters: Current filter state object
 * - onFiltersChange: Callback when filters change
 * - onClear: Callback to clear all filters
 * - teamMembers: Array of team members for assignee filter
 * - isOpen: Boolean to control filter panel visibility
 * - onClose: Callback to close filter panel
 */
const TicketFilters = ({
  filters = {},
  onFiltersChange,
  onClear,
  teamMembers = [],
  isOpen = false,
  onClose
}) => {
  // Local state for filters
  const [localFilters, setLocalFilters] = useState({
    status: filters.status || [],
    priority: filters.priority || [],
    assignee: filters.assignee || '',
    dateFrom: filters.dateFrom || '',
    dateTo: filters.dateTo || '',
    search: filters.search || ''
  });

  // Update local filters when props change
  useEffect(() => {
    setLocalFilters({
      status: filters.status || [],
      priority: filters.priority || [],
      assignee: filters.assignee || '',
      dateFrom: filters.dateFrom || '',
      dateTo: filters.dateTo || '',
      search: filters.search || ''
    });
  }, [filters]);

  // Status options
  const statusOptions = [
    { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' },
    { value: 'open', label: 'Open', color: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' },
    { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' },
    { value: 'resolved', label: 'Resolved', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400' },
    { value: 'closed', label: 'Closed', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' }
  ];

  // Priority options
  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'text-gray-600 dark:text-gray-400' },
    { value: 'medium', label: 'Medium', color: 'text-blue-600 dark:text-blue-400' },
    { value: 'high', label: 'High', color: 'text-orange-600 dark:text-orange-400' },
    { value: 'urgent', label: 'Urgent', color: 'text-red-600 dark:text-red-400' }
  ];

  // Handle status toggle
  const toggleStatus = (status) => {
    const newStatus = localFilters.status.includes(status)
      ? localFilters.status.filter(s => s !== status)
      : [...localFilters.status, status];
    
    setLocalFilters(prev => ({ ...prev, status: newStatus }));
  };

  // Handle priority toggle
  const togglePriority = (priority) => {
    const newPriority = localFilters.priority.includes(priority)
      ? localFilters.priority.filter(p => p !== priority)
      : [...localFilters.priority, priority];
    
    setLocalFilters(prev => ({ ...prev, priority: newPriority }));
  };

  // Handle assignee change
  const handleAssigneeChange = (e) => {
    setLocalFilters(prev => ({ ...prev, assignee: e.target.value }));
  };

  // Handle date change
  const handleDateChange = (field, value) => {
    setLocalFilters(prev => ({ ...prev, [field]: value }));
  };

  // Apply filters
  const handleApply = () => {
    if (onFiltersChange) {
      onFiltersChange(localFilters);
    }
    if (onClose) {
      onClose();
    }
  };

  // Clear all filters
  const handleClear = () => {
    const emptyFilters = {
      status: [],
      priority: [],
      assignee: '',
      dateFrom: '',
      dateTo: '',
      search: ''
    };
    setLocalFilters(emptyFilters);
    if (onClear) {
      onClear();
    }
  };

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    if (localFilters.status.length > 0) count++;
    if (localFilters.priority.length > 0) count++;
    if (localFilters.assignee) count++;
    if (localFilters.dateFrom || localFilters.dateTo) count++;
    if (localFilters.search) count++;
    return count;
  };

  const activeCount = getActiveFilterCount();

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Filter Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white dark:bg-gray-900 shadow-2xl z-50 overflow-y-auto">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <Filter className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Filters
              </h2>
              {activeCount > 0 && (
                <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold text-white bg-indigo-600 rounded-full">
                  {activeCount}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Filter Content */}
          <div className="flex-1 px-6 py-4 space-y-6 overflow-y-auto">
            {/* Status Filter */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Status
              </label>
              <div className="space-y-2">
                {statusOptions.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                  >
                    <div className="relative flex items-center">
                      <input
                        type="checkbox"
                        checked={localFilters.status.includes(option.value)}
                        onChange={() => toggleStatus(option.value)}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700"
                      />
                    </div>
                    <span className={`text-sm font-medium px-2 py-1 rounded ${option.color}`}>
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Priority Filter */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Priority
              </label>
              <div className="space-y-2">
                {priorityOptions.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                  >
                    <div className="relative flex items-center">
                      <input
                        type="checkbox"
                        checked={localFilters.priority.includes(option.value)}
                        onChange={() => togglePriority(option.value)}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700"
                      />
                    </div>
                    <span className={`text-sm font-medium capitalize ${option.color}`}>
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Assignee Filter */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Assignee
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={localFilters.assignee}
                  onChange={handleAssigneeChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                >
                  <option value="">All Assignees</option>
                  <option value="unassigned">Unassigned</option>
                  {teamMembers.map((member) => (
                    <option key={member.user_id} value={member.user_id}>
                      {member.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Date Range Filter */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Created Date Range
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="date"
                    value={localFilters.dateFrom}
                    onChange={(e) => handleDateChange('dateFrom', e.target.value)}
                    placeholder="From"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                  />
                </div>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="date"
                    value={localFilters.dateTo}
                    onChange={(e) => handleDateChange('dateTo', e.target.value)}
                    placeholder="To"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>
              {localFilters.dateFrom && localFilters.dateTo && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {new Date(localFilters.dateFrom).toLocaleDateString()} - {new Date(localFilters.dateTo).toLocaleDateString()}
                </p>
              )}
            </div>

            {/* Active Filters Summary */}
            {activeCount > 0 && (
              <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-indigo-900 dark:text-indigo-300 mb-2">
                      Active Filters ({activeCount})
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {localFilters.status.length > 0 && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/40 rounded">
                          Status: {localFilters.status.length}
                        </span>
                      )}
                      {localFilters.priority.length > 0 && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/40 rounded">
                          Priority: {localFilters.priority.length}
                        </span>
                      )}
                      {localFilters.assignee && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/40 rounded">
                          Assignee
                        </span>
                      )}
                      {(localFilters.dateFrom || localFilters.dateTo) && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/40 rounded">
                          Date Range
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={handleClear}
                className="flex-1"
                disabled={activeCount === 0}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Clear All
              </Button>
              <Button
                onClick={handleApply}
                className="flex-1"
              >
                <Check className="w-4 h-4 mr-2" />
                Apply Filters
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TicketFilters;
